import { num, type ComponentForm, type FieldDef } from '../component-types.js';

/**
 * `postProcess` (Unity URP's Volume): global or local box volumes that
 * override post-processing effects, blended by priority / weight / distance
 * over `settings.render.postProcess` (see engine/docs/systems/cameras-and-post.md).
 * Absent fields keep the value from lower-priority volumes / the project.
 */
export const POST_PROFILE_FIELDS: FieldDef[] = [
  {
    key: 'bloom',
    label: 'Bloom',
    type: {
      kind: 'object',
      fields: [
        { key: 'intensity', label: 'Intensity', type: num(0.05, { min: 0 }), hint: '0 = off.' },
        { key: 'threshold', label: 'Threshold', type: num(0.05, { min: 0 }), hint: 'Linear brightness where blooming starts (1 = white).' },
        { key: 'knee', label: 'Soft knee', type: num(0.05, { min: 0 }) },
        { key: 'scatter', label: 'Scatter', type: num(0.05, { min: 0, max: 1 }), hint: 'How far the glow spreads.' },
        { key: 'tint', label: 'Tint', type: { kind: 'color', alpha: false } },
      ],
    },
  },
  {
    key: 'tonemapping',
    label: 'Tonemapping',
    type: { kind: 'object', fields: [{ key: 'mode', label: 'Mode', type: { kind: 'enum', options: ['none', 'aces', 'neutral', 'reinhard'] } }] },
  },
  {
    key: 'colorAdjustments',
    label: 'Color Adjustments',
    type: {
      kind: 'object',
      fields: [
        { key: 'postExposure', label: 'Post exposure (EV)', type: num(0.1, { min: -20, max: 20 }) },
        { key: 'contrast', label: 'Contrast', type: num(1, { min: -100, max: 100 }) },
        { key: 'saturation', label: 'Saturation', type: num(1, { min: -100, max: 100 }) },
        { key: 'hueShift', label: 'Hue shift', type: num(1, { min: -180, max: 180 }) },
        { key: 'colorFilter', label: 'Color filter', type: { kind: 'color', alpha: false } },
      ],
    },
  },
  {
    key: 'whiteBalance',
    label: 'White Balance',
    type: {
      kind: 'object',
      fields: [
        { key: 'temperature', label: 'Temperature', type: num(1, { min: -100, max: 100 }) },
        { key: 'tint', label: 'Tint', type: num(1, { min: -100, max: 100 }) },
      ],
    },
  },
  {
    key: 'vignette',
    label: 'Vignette',
    type: {
      kind: 'object',
      fields: [
        { key: 'intensity', label: 'Intensity', type: num(0.05, { min: 0, max: 1 }) },
        { key: 'smoothness', label: 'Smoothness', type: num(0.05, { min: 0.01, max: 1 }) },
        { key: 'color', label: 'Color', type: { kind: 'color', alpha: false } },
        { key: 'center', label: 'Center', type: { kind: 'json' }, hint: '[x, y] screen fractions; [0.5, 0.5] = middle.' },
      ],
    },
  },
  {
    key: 'filmGrain',
    label: 'Film Grain',
    type: {
      kind: 'object',
      fields: [
        { key: 'intensity', label: 'Intensity', type: num(0.05, { min: 0, max: 1 }) },
        { key: 'response', label: 'Response', type: num(0.05, { min: 0, max: 1 }), hint: 'How much grain fades out in bright areas.' },
      ],
    },
  },
  {
    key: 'chromaticAberration',
    label: 'Chromatic Aberration',
    type: { kind: 'object', fields: [{ key: 'intensity', label: 'Intensity', type: num(0.05, { min: 0, max: 1 }) }] },
  },
  { key: 'dithering', label: 'Dithering', type: { kind: 'bool' }, hint: 'Noise against banding in smooth gradients.' },
  {
    key: 'distortion',
    label: 'Distortion',
    type: {
      kind: 'object',
      fields: [
        { key: 'strength', label: 'Strength', type: num(0.001, { min: 0, max: 0.2 }), hint: 'Screen offset (0.005..0.02 underwater, heat haze).' },
        { key: 'scale', label: 'Scale', type: num(0.5, { min: 0 }), hint: 'Waves across the screen (default 3).' },
        { key: 'speed', label: 'Speed', type: num(0.1), hint: 'Animation speed (default 1.5).' },
      ],
    },
  },
  {
    key: 'blur',
    label: 'Blur',
    type: { kind: 'object', fields: [{ key: 'radius', label: 'Radius (px)', type: num(0.5, { min: 0, max: 32 }) }] },
  },
  {
    key: 'ambientOcclusion',
    label: 'Ambient Occlusion',
    type: {
      kind: 'object',
      fields: [
        { key: 'intensity', label: 'Intensity', type: num(0.05, { min: 0, max: 1 }), hint: 'Contact shadows in creases, under objects, inside foliage (0 = off).' },
        { key: 'radius', label: 'Radius (m)', type: num(0.05, { min: 0.01, max: 20 }), hint: 'Default 0.6.' },
        { key: 'power', label: 'Power', type: num(0.1, { min: 0.1, max: 8 }), hint: 'Contrast (default 1.5).' },
      ],
    },
  },
  {
    key: 'temporalAntiAliasing',
    label: 'Temporal Anti-aliasing',
    type: {
      kind: 'object',
      fields: [
        { key: 'enabled', label: 'Enabled', type: { kind: 'bool' }, hint: 'Removes shimmer on grass, leaves, wires and thin edges.' },
        { key: 'feedback', label: 'Feedback', type: num(0.01, { min: 0, max: 0.98 }), hint: 'History weight (default 0.9; higher = smoother, more ghosting).' },
      ],
    },
  },
  {
    key: 'sunShafts',
    label: 'Sun Shafts',
    type: {
      kind: 'object',
      fields: [
        { key: 'intensity', label: 'Intensity', type: num(0.1, { min: 0, max: 20 }), hint: 'God rays from the sun through gaps (0 = off).' },
        { key: 'length', label: 'Length', type: num(0.05, { min: 0, max: 1 }) },
        { key: 'decay', label: 'Decay', type: num(0.005, { min: 0.5, max: 1 }) },
        { key: 'tint', label: 'Tint', type: { kind: 'color', alpha: false } },
      ],
    },
  },
];

const form: ComponentForm = {
  key: 'postProcess',
  label: 'Post-process Volume',
  icon: 'camera',
  order: 32,
  defaults: { global: true, bloom: { intensity: 0.3, threshold: 1 }, vignette: { intensity: 0.2 } },
  fields: [
    {
      key: 'global',
      label: 'Global',
      type: { kind: 'bool' },
      hint: 'Applies everywhere. Off: only inside the volume box (size, else the box collider) plus the blend distance.',
      read: (v) => v.global ?? true,
    },
    { key: 'priority', label: 'Priority', type: num(1), hint: 'Higher-priority volumes apply last (win).' },
    { key: 'weight', label: 'Weight', type: num(0.05, { min: 0, max: 1 }), read: (v) => v.weight ?? 1 },
    { key: 'blendDistance', label: 'Blend distance', type: num(0.5, { min: 0 }), showIf: (v) => v.global === false },
    { key: 'size', label: 'Box size', type: { kind: 'vec3' }, showIf: (v) => v.global === false },
    ...POST_PROFILE_FIELDS,
  ],
};
export default form;
